import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { canGoLive, pauseWhenOffscreen } from '@theme/motion-lifecycle';

/**
 * @typedef {object} ParallaxHeroRefs
 * @property {HTMLElement[]} layers - Image layers, each with data-depth (px of travel)
 */

/**
 * Parallax hero (ported from INVIZI-REBUILT): while the hero is on screen, each layer moves up by up to its
 * data-depth in pixels (40 for the back layer up to 200 for the front), in step with how far the hero has
 * scrolled, reaching it when the hero is half off the screen.
 *
 * PERF-07: the server-rendered layers are the static design (data-motion="static"). The hero only goes live
 * when the tier gate allows motion and the visitor hasn't asked for reduced motion (R1); the move is
 * visually continuous with the static frame, so it may start in view (R3). Geometry is measured on resize,
 * never inside the scroll frame, which only writes transforms. Scrolling is followed only while the hero is on
 * screen (R4), and after a perf:downgrade it settles back to static once it is off screen (R5).
 *
 * @extends {Component<ParallaxHeroRefs>}
 */
class ParallaxHeroComponent extends Component {
  requiredRefs = ['layers'];

  #frame = 0;
  #listening = false;
  #downgraded = false;
  /** @type {(() => void) | null} */
  #stopWatching = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** Hero top in document coordinates, its height and the viewport height, refreshed on resize. */
  #geometry = { top: 0, height: 1, viewport: 1 };

  connectedCallback() {
    super.connectedCallback();
    if (prefersReducedMotion() || !canGoLive()) return;

    this.dataset.motion = 'live';
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this);
    this.#measure();
    this.#stopWatching = pauseWhenOffscreen(this, { onPause: this.#onPause, onResume: this.#onResume });
    document.addEventListener('perf:downgrade', this.#onDowngrade);
    // pauseWhenOffscreen only reports changes, so start following the page if the hero is already in view.
    if (!this.hasAttribute('data-offscreen')) this.#onResume();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #teardown() {
    this.#stopWatching?.();
    this.#stopWatching = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    document.removeEventListener('perf:downgrade', this.#onDowngrade);
    this.#stopListening();
  }

  #measure() {
    const rect = this.getBoundingClientRect();
    this.#geometry = { top: rect.top + window.scrollY, height: Math.max(1, rect.height), viewport: window.innerHeight };
    if (this.#listening) this.#schedule();
  }

  #onResume = () => {
    if (this.#downgraded || this.#listening) return;
    this.#listening = true;
    window.addEventListener('scroll', this.#schedule, { passive: true });
    this.#schedule();
  };

  #onPause = () => {
    this.#stopListening();
    if (this.#downgraded) this.#settle();
  };

  #onDowngrade = () => {
    this.#downgraded = true;
    if (this.hasAttribute('data-offscreen')) this.#settle();
  };

  #stopListening() {
    if (!this.#listening) return;
    this.#listening = false;
    window.removeEventListener('scroll', this.#schedule);
    cancelAnimationFrame(this.#frame);
  }

  /** Back to the server-rendered frame and static for the rest of this page view. */
  #settle() {
    for (const layer of this.refs.layers) layer.style.transform = '';
    this.dataset.motion = 'static';
    this.#teardown();
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  /**
   * 0 at the hero's resting place (where it sits with the page at the top, or the bottom of the screen if it
   * starts lower), 1 once the hero is half off the top of the screen. Reads only scrollY.
   */
  #update = () => {
    const { top, height, viewport } = this.#geometry;
    const topInView = top - window.scrollY;
    const start = Math.min(viewport, top);
    const end = -height / 2;
    const progress = Math.min(1, Math.max(0, (start - topInView) / Math.max(1, start - end)));

    for (const layer of this.refs.layers) {
      const depth = Number(layer.dataset.depth) || 0;
      layer.style.transform = `translate3d(0, ${(-progress * depth).toFixed(1)}px, 0)`;
    }
  };
}

if (!customElements.get('parallax-hero-component')) {
  customElements.define('parallax-hero-component', ParallaxHeroComponent);
}
